import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type MediaCategory =
  | 'TEAM_LOGO'
  | 'SPONSOR_LOGO'
  | 'PARTNER_LOGO'
  | 'COMPETITION_LOGO'
  | 'HERO_BANNER'
  | 'OTHER'
  | 'PLAYER_PHOTO'
  | 'TEAM_PHOTO'
  | 'FANZONE_PHOTO';

export interface MediaAssetDto {
  id: string;
  category: MediaCategory;
  label: string;
  url: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  position: number;
  ctaUrl: string | null;
  createdAt: string;
}

export interface PresignResult {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  expiresAt: string;
}

interface ListFilters {
  category?: MediaCategory;
  q?: string;
}

const QK = {
  list: (f: ListFilters) => ['mediaAssets', f] as const,
};

export function useMediaAssets(filters: ListFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters.category) params.category = filters.category;
      if (filters.q) params.q = filters.q;
      const res = await apiClient.get<{ data: MediaAssetDto[] }>('/media-assets', { params });
      return res.data.data;
    },
  });
}

export function usePresignMedia() {
  return useMutation({
    mutationFn: async (input: {
      category: MediaCategory;
      contentType: string;
      filename: string;
      sizeBytes: number;
    }) => {
      const res = await apiClient.post<PresignResult>('/media-assets/presign', input);
      return res.data;
    },
  });
}

export function useConfirmMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      category: MediaCategory;
      label: string;
      s3Key: string;
      mimeType: string;
      sizeBytes: number;
      width?: number;
      height?: number;
      position?: number;
      ctaUrl?: string;
    }) => {
      const res = await apiClient.post<MediaAssetDto>('/media-assets', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mediaAssets'] }),
  });
}

export function useUpdateMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: { label?: string; position?: number; ctaUrl?: string | null };
    }) => {
      const res = await apiClient.patch<MediaAssetDto>(`/media-assets/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mediaAssets'] }),
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/media-assets/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mediaAssets'] }),
  });
}

export interface MediaAssetUsage {
  entity: string;
  entityId: string;
  name: string;
}

export function useMediaAssetUsages(id: string | null) {
  return useQuery({
    queryKey: ['mediaAssets', id, 'usages'],
    queryFn: async () => {
      const res = await apiClient.get<{ usages: MediaAssetUsage[] }>(`/media-assets/${id}/usages`);
      return res.data.usages;
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

// Pre-filter client-side: riduce solo file molto grandi (>5MB o >4000px)
// per risparmiare banda. L'ottimizzazione reale avviene server-side (Sharp).
const CLIENT_MAX_DIM = 2500;
const CLIENT_MAX_BYTES = 5 * 1024 * 1024;

async function preFilterImageFile(file: File): Promise<File> {
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  if (file.size <= CLIENT_MAX_BYTES) return file; // già abbastanza piccolo
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, CLIENT_MAX_DIM / Math.max(width, height));
    if (scale >= 1) { bitmap.close(); return file; }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Canvas failed')); return; }
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.92,
      );
    });
  } catch {
    return file;
  }
}

/**
 * PUT del file su S3 via XMLHttpRequest (non fetch): è l'unica API browser che
 * espone l'evento di progresso in UPLOAD (fetch ha solo il progresso in
 * download). Risolve su 2xx, altrimenti rigetta con lo status HTTP.
 */
export function xhrPutUpload(
  url: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.send(file);
  });
}

/** Upload helper: pre-filter → presign → PUT (con progresso) → confirm.
 *  L'ottimizzazione finale (resize + JPEG 85%) avviene server-side via Sharp.
 *  onProgress è opzionale: chi non lo passa mantiene il comportamento invariato. */
export async function uploadMediaAsset(
  category: MediaCategory,
  label: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<MediaAssetDto> {
  const optimized = await preFilterImageFile(file);

  const presignRes = await apiClient.post<PresignResult>('/media-assets/presign', {
    category,
    contentType: optimized.type,
    filename: optimized.name,
    sizeBytes: optimized.size,
  });
  const { uploadUrl, key } = presignRes.data;

  await xhrPutUpload(uploadUrl, optimized, optimized.type, onProgress);

  const dims = await readImageDimensions(optimized).catch(() => null);
  const confirm = await apiClient.post<MediaAssetDto>('/media-assets', {
    category,
    label,
    s3Key: key,
    mimeType: optimized.type,
    sizeBytes: optimized.size,
    width: dims?.width,
    height: dims?.height,
  });
  return confirm.data;
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      reject(new Error('not a raster image'));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.width, height: img.height }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}
